import Backbone from 'backbone';

import Type from 'molbio/maps/Type';

interface LayoutAttributes {
  vMargin: number;
}

interface Layout {
  get<K extends keyof LayoutAttributes>(key: K): LayoutAttributes[K];
  set<K extends keyof LayoutAttributes>(
    attributeName: K,
    value: LayoutAttributes[K],
    options?: Backbone.ModelSetOptions
  ): Layout;
}

abstract class Layout extends Backbone.Model {
  vMargin(): number {
    return this.get('vMargin');
  }
}

interface RingLayoutAttributes extends LayoutAttributes {
  type: Type;
  /**
   * 1 means this ring grows outwards. -1 means this ring grows inwards. This is NOT the same thing as Strand/
   * StrandOfDoubleStranded, which is also represented by one or negative one.
   */
  dir: 1 | -1;
  innerRadius: number;
  outerRadius: number;
  ringHeight: number;
  minRingHeight: number;
  maxRingHeight: number;
  visible: boolean;
  inlineLabelsVisible?: boolean;
}

interface RingLayout {
  get<K extends keyof RingLayoutAttributes>(key: K): RingLayoutAttributes[K];
  set<K extends keyof RingLayoutAttributes>(
    attributeName: K,
    value: RingLayoutAttributes[K],
    options?: Backbone.ModelSetOptions
  ): RingLayout;
}

class RingLayout extends Layout {
  override defaults(): RingLayoutAttributes {
    return {
      type: new Type(12),
      dir: 1, // 1 means grows outwards, -1 means grows inwards.
      innerRadius: 0,
      outerRadius: 0,
      ringHeight: 15,
      minRingHeight: 15,
      maxRingHeight: 15,
      visible: true,
      vMargin: 2,
    };
  }
}

class ORFLayout extends RingLayout {
  override defaults(): RingLayoutAttributes {
    return {
      ...super.defaults(),
      ringHeight: 8,
      minRingHeight: 8,
      maxRingHeight: 8,
    };
  }
}

interface LayoutWithDNAAttributes extends RingLayoutAttributes {
  DNA: null | RingLayout;
}

interface SearchLayout {
  get<K extends keyof LayoutWithDNAAttributes>(key: K): LayoutWithDNAAttributes[K];
  set<K extends keyof LayoutWithDNAAttributes>(
    attributeName: K,
    value: LayoutWithDNAAttributes[K],
    options?: Backbone.ModelSetOptions
  ): SearchLayout;
}

class SearchLayout extends RingLayout {
  override defaults(): LayoutWithDNAAttributes {
    return {
      ...super.defaults(),
      ringHeight: 0,
      vMargin: 0,
      DNA: null,
    };
  }
}

interface BiointelligentLinkHighlightLayout {
  get<K extends keyof LayoutWithDNAAttributes>(key: K): LayoutWithDNAAttributes[K];
  set<K extends keyof LayoutWithDNAAttributes>(
    attributeName: K,
    value: LayoutWithDNAAttributes[K],
    options?: Backbone.ModelSetOptions
  ): BiointelligentLinkHighlightLayout;
}

class BiointelligentLinkHighlightLayout extends RingLayout {
  override defaults(): LayoutWithDNAAttributes {
    return {...super.defaults(), ringHeight: 15, DNA: null};
  }
}

interface LabelLayoutAttributes extends RingLayoutAttributes {
  annotationsVisible: boolean;
  translationsVisible: boolean;
  partAnnotationsVisible: boolean;
  primersVisible: boolean;
  cutSitesVisible: boolean;
}

interface LabelLayout {
  get<K extends keyof LabelLayoutAttributes>(key: K): LabelLayoutAttributes[K];
  set<K extends keyof LabelLayoutAttributes>(
    attributeName: K,
    value: LabelLayoutAttributes[K],
    options?: Backbone.ModelSetOptions
  ): LabelLayout;
}

class LabelLayout extends RingLayout {
  override defaults(): LabelLayoutAttributes {
    return {
      ...super.defaults(),
      annotationsVisible: true,
      translationsVisible: true,
      partAnnotationsVisible: true,
      primersVisible: true,
      cutSitesVisible: true,
    };
  }
}

interface AxisLayoutAttributes extends RingLayoutAttributes {
  tickSize: number;
  type: Type;
}

interface AxisLayout {
  get<K extends keyof AxisLayoutAttributes>(key: K): AxisLayoutAttributes[K];
  set<K extends keyof AxisLayoutAttributes>(
    attributeName: K,
    value: AxisLayoutAttributes[K],
    options?: Backbone.ModelSetOptions
  ): AxisLayout;
}

class AxisLayout extends RingLayout {
  override defaults(): AxisLayoutAttributes {
    return {
      ...super.defaults(),
      tickSize: 7,
      type: new Type(11),
      vMargin: 20,
    };
  }

  height(): number {
    return this.get('type').height() + this.get('tickSize') + 2;
  }

  boxHeight(): number {
    return this.height() + this.vMargin();
  }
}

interface SelectionLayoutAttributes {
  triangleWidth: number;
}

interface SelectionLayout {
  get<K extends keyof SelectionLayoutAttributes>(key: K): SelectionLayoutAttributes[K];
  set<K extends keyof SelectionLayoutAttributes>(
    attributeName: K,
    value: SelectionLayoutAttributes[K],
    options?: Backbone.ModelSetOptions
  ): SelectionLayout;
}

class SelectionLayout extends Backbone.Model {
  override defaults(): SelectionLayoutAttributes {
    return {triangleWidth: 8.5};
  }
}

interface TitleLayoutAttributes {
  minFontSize: number;
  maxFontSize: number;
  type: Type;
}

interface TitleLayout {
  get<K extends keyof TitleLayoutAttributes>(key: K): TitleLayoutAttributes[K];
  set<K extends keyof TitleLayoutAttributes>(
    attributeName: K,
    value: TitleLayoutAttributes[K],
    options?: Backbone.ModelSetOptions
  ): TitleLayout;
}

class TitleLayout extends Backbone.Model {
  override defaults(): TitleLayoutAttributes {
    return {
      minFontSize: 14,
      maxFontSize: 23,
      type: new Type(14),
    };
  }
}

export interface CircularLayout {
  AxisLayout: AxisLayout;
  HomologyAxisLayout: AxisLayout;
  DNALayout: RingLayout;
  HomologyDNALayout: RingLayout;
  SearchLayout: SearchLayout;
  BiointelligentLinkHighlightLayout: BiointelligentLinkHighlightLayout;
  CircularPrimersLayout: RingLayout;
  CircularAnnotationsLayout: RingLayout;
  CircularTranslationsLayout: RingLayout;
  CircularPartAnnotationsLayout: RingLayout;
  CircularORFsLayout: ORFLayout;
  SelectionLayout: SelectionLayout;
  LabelLayout: LabelLayout;
  TitleLayout: TitleLayout;
}

export default function (): CircularLayout {
  const dnaLayout = new RingLayout();
  const labelLayout = new LabelLayout();
  labelLayout.get('type').setFontSize(14);
  return {
    AxisLayout: new AxisLayout(),
    HomologyAxisLayout: new AxisLayout(),
    DNALayout: dnaLayout,
    HomologyDNALayout: new RingLayout(),
    SearchLayout: new SearchLayout({DNA: dnaLayout}),
    BiointelligentLinkHighlightLayout: new BiointelligentLinkHighlightLayout({DNA: dnaLayout}),
    CircularPrimersLayout: new RingLayout(),
    CircularAnnotationsLayout: new RingLayout({dir: -1}),
    CircularTranslationsLayout: new RingLayout({dir: -1}),
    CircularPartAnnotationsLayout: new RingLayout({dir: -1}),
    CircularORFsLayout: new ORFLayout(),
    SelectionLayout: new SelectionLayout(),
    LabelLayout: labelLayout,
    TitleLayout: new TitleLayout(),
  };
}

export {AxisLayout, LabelLayout, ORFLayout, SearchLayout, BiointelligentLinkHighlightLayout, RingLayout};
